/**
 * §4 마이크 캡처 파이프라인 (메인 스레드 측).
 *
 *   getUserMedia → AudioContext(16k 시도) → AudioWorklet → Framer → WS
 *
 * 여기가 소유하는 정책:
 *  - `AudioContext({sampleRate:16000})` 1차, 실패 시 워크릿 FIR 폴백(§4)
 *  - `ws.bufferedAmount > 256KB` 면 **가장 오래된 미전송 프레임부터 드롭**하고
 *    seq 를 건너뛰어 서버가 갭을 보게 한다 — 브라우저에 오디오를 쌓아
 *    시차를 누적시키지 않는다(agy C-3)
 *  - 뮤트 = 트랙 disable(서버 도달 전 차단)
 *
 * ── 짧은 단절은 버린다가 아니라 **버틴다**(2026-08-05 라이브 진단) ────────
 *
 * 실사용 신고: "중간중간 번역을 안 하고 멍때릴 때가 있는데, 호스트가
 * 새로고침하면 정상으로 돌아온다." 새로고침이 고친다는 것은 **서버가 아니라
 * 이 페이지의 오디오 경로가 죽었다**는 뜻이다. 원인은 두 갈래였다:
 *
 *  1. 소켓이 끊겨도(`ready=false`) 워크릿은 계속 돌고, emit() 은 프레임을
 *     전량 드롭했다. 재접속이 성공해도 **아무도 캡처를 다시 물리지 않아서**
 *     소켓만 살고 오디오는 영영 안 갔다 — 화면상 마이크는 "켜짐"이고
 *     레벨 미터도 움직인다(둘 다 클라이언트 계산이다). 완벽한 무증상 고장.
 *  2. 트랙/AudioContext 자체가 죽는 경우(장치 전환·블루투스 핸드오프·OS 절전·
 *     탭 백그라운드화로 인한 suspend). 아무도 관측하지 않았다.
 *
 * 그래서 이 클래스는 이제 **스스로 살아 있는지 감시**한다:
 *  - `MediaStreamTrack` 의 ended/mute 와 `AudioContext.state` 를 구독하고,
 *    워크릿 블록이 `STALL_MS` 동안 안 오면 죽은 것으로 판정한다.
 *  - 죽으면 `onBroken` 으로 상위에 알리고 `restart()` 로 전 경로를 새로 만든다.
 *  - 소켓이 잠깐 끊긴 동안(`ready=false`)에는 프레임을 **버리지 않고**
 *    `STALL_BUFFER_FRAMES` 만큼 들고 있다가 재개되면 순서대로 흘려보낸다.
 *    사용자의 오랜 요구가 "지연이 생기더라도 누락은 안 된다" 이므로,
 *    수 초짜리 블립은 손실 0 으로 넘긴다. 그 이상은 오래된 것부터 버리고
 *    seq 를 건너뛰어 서버가 갭으로 계수하게 한다(조용한 유실 금지, §1 원칙 3).
 */

import workletUrl from '../worklet/capture-processor.ts?worker&url'
import { FlacEncoderSession, isFlacSupported } from './flac-encoder'
import {
  Framer,
  LEGACY_FRAME_BYTES,
  OTF1_NSAMP,
  packCompressed,
  SEQ_HEADER_BYTES,
  type Frame,
} from './framer'
import {
  blockDbfs,
  blockPeak,
  floatToPcm16,
  rmsToDbfs,
  SILENCE_DBFS,
  TARGET_RATE,
} from './resampler'
import type { CaptureMessage } from '../worklet/capture-processor'

/** §4 클라이언트 적체 문턱 — 약 8초분 */
export const BUFFER_LIMIT_BYTES = 256 * 1024

/**
 * 소켓이 끊긴 동안 들고 있을 프레임 수 — 320ms × 25 = 8초.
 *
 * **왜 25 인가**: 재개 시 보류분 전량을 `drainStalled()` 가 연달아 `emit()`
 * 하는데, 그 배출 자체가 `bufferedAmount` 를 밀어 올려 **배출 도중 적체
 * 드롭**이 걸릴 수 있었다(C1). 25 는 그 경로를 산술로 닫는 최대값이다:
 *
 *     25 × LEGACY_FRAME_BYTES = 25 × 10,244 = 256,100 ≤ 262,144 = BUFFER_LIMIT_BYTES
 *     26 × 10,244 = 266,344 > 262,144
 *
 * 즉 bufferedAmount 가 0 에서 출발하는 최악의 경우에도 마지막 프레임까지
 * 문턱 아래에 들어간다. 이 등식이 이 상수의 **정의**이므로 주석이 아니라
 * 테스트가 지킨다(`capture-policy.test.ts` C1 회귀 가드).
 *
 * 재접속 백오프가 1→8s 이므로 한 번의 블립은 대부분 이 안에서 끝난다.
 * 넘치면 **가장 오래된 것부터** 버린다 — 최신 음성이 회의의 현재이기
 * 때문이다. 버린 만큼은 갭으로 표면화한다(조용한 유실 금지).
 */
export const STALL_BUFFER_FRAMES = 25

/**
 * 워크릿 블록이 이만큼 안 오면 캡처가 죽은 것으로 본다.
 *
 * 정상 동작에서 블록은 128 샘플(≈2.7ms @48k)마다 온다. 1.5초는 그 500배가
 * 넘으므로 오탐이 아니다 — 탭이 백그라운드로 가 스로틀링돼도 이 정도로는
 * 멈추지 않고, 진짜 멈춘 경우(suspended·트랙 종료)만 걸린다.
 */
export const STALL_MS = 1_500

/**
 * 복구 판정용 신호 바닥(dBFS) — **경고 문턱이 아니다**.
 *
 * 이것은 재시작 후 "소리가 돌아왔다" 를 선언하는 **복구 판정** 문턱이다
 * (`onSamples` 의 `awaitingSignal` 해제). 서버 VAD 에너지 바닥(정본은
 * `server/vad.py`, 기본 −55)과 **다른 축이며 같게 만들려는 "정리"
 * 리팩터를 이 주석이 금지한다**:
 *
 *   바닥을 −55 로 내리면 **잡음 바닥만으로 복구를 선언**해, 장치가 회수된
 *   뒤 무음만 내보내는 죽은 마이크를 화면이 "정상" 으로 그린다 — 위
 *   `restart()` 주석이 기록한 그 사고다. 복구 판정은 보수적인 쪽이 안전하다.
 *
 * (레벨 미터의 저입력 경고는 서버가 caps 로 알려 주는 바닥을 따른다 —
 * `resampler.ts` 의 `DEFAULT_VAD_FLOOR_DBFS`. 이 값과 무관하다.)
 */
export const SIGNAL_FLOOR_DBFS = -45

/**
 * §4 브라우저가 **실제로 적용한** 캡처 처리(getSettings() 보고값).
 *
 * 요청(`getUserMedia` 제약)과 적용은 다른 것이다. 제약은 희망사항이고,
 * 장치·브라우저·OS 가 셋 중 무엇이든 무시할 수 있다 — 그리고 무시해도
 * **아무 오류도 나지 않는다**. 그래서 요청만 보고 "AGC 가 켜져 있다" 고
 * 말할 수 없다.
 *
 * `undefined` 는 "모른다" 이지 "꺼졌다" 가 아니다. 브라우저가 그 키를
 * 보고하지 않으면(Safari 는 일부를 안 준다) 없는 정보를 지어내지 않는다.
 */
export interface AppliedAudioProcessing {
  echoCancellation?: boolean
  noiseSuppression?: boolean
  autoGainControl?: boolean
  /** 트랙이 실제로 내보내는 채널 수 */
  channelCount?: number
  /** 장치·브라우저가 이 트랙에 대해 지원한다고 밝힌 항목(있을 때만) */
  supportsAutoGainControl?: boolean
}

export interface CaptureStats {
  /** 전송한 프레임 수 */
  sent: number
  /**
   * 캡처가 **만든** 프레임의 단조 카운터. 전송 결과와 무관하다 — 전송·드롭·
   * 단절 보류(stalled)를 모두 포함한다.
   *
   * `sent + dropped` 로 대신하면 단절 보류 중인 프레임이 어느 쪽에도 안 들어가
   * 최대 `STALL_BUFFER_FRAMES`(8 s)의 활성 입력이 안 세어진다(R2). D4 잡음 바닥
   * 표본은 "새 오디오가 왔는가" 를 물으므로 이 카운터를 봐야 한다.
   */
  framesGenerated: number
  /**
   * 드롭한 프레임 **합계**(사유 무관). 불변식:
   * `dropped === droppedBacklog + droppedStallEvict`.
   */
  dropped: number
  /** 적체 드롭(소켓은 살아 있는데 대역이 못 따라감 — **최신** 프레임을 버린다) */
  droppedBacklog: number
  /** 단절 보류 큐 축출(`STALL_BUFFER_FRAMES` 초과 — **가장 오래된** 것을 버린다) */
  droppedStallEvict: number
  /** 현재 WS 송신 버퍼 크기 */
  buffered: number
  /** 실제 캡처 샘플레이트(16000 이면 네이티브 경로) */
  sampleRate: number
  /** true = 워크릿 FIR 폴백 사용 중 */
  resampled: boolean
  muted: boolean
  /** §4 수음 레벨(dBFS, RMS) — 화면 미터가 쓴다 */
  dbfs: number
  /** 피크 dBFS(클리핑 판정용 — RMS 로는 사인파 왜곡을 못 잡는다) */
  peakDbfs: number
  /**
   * 소켓 단절로 **보류 중**인 프레임 수. 0 보다 크면 지금 오디오가 서버에
   * 안 가고 있다는 뜻이다 — 레벨 미터만으로는 절대 알 수 없는 사실이라
   * 화면이 이 값을 보고 "재연결 중" 을 말할 수 있어야 한다.
   */
  stalled: number
  /**
   * 마지막으로 워크릿 블록을 받은 뒤 흐른 ms. 이 값이 STALL_MS 를 넘으면
   * 마이크가 죽은 것이다(레벨 미터는 마지막 값에 얼어붙은 채 남는다).
   */
  lastBlockAgeMs: number
  /** 캡처가 죽어 자동 복구 중인가 */
  broken: boolean
  /**
   * 브라우저가 실제로 적용한 처리(§4). 요청은 `open()` 이 항상 같은 값으로
   * 하므로 화면·로그가 알아야 하는 것은 **적용 결과**다. null 이면 아직
   * 트랙이 없다(캡처 시작 전).
   *
   * 선택 항목이다 — 진단용 부가 정보이므로, 상태를 만들어 내는 쪽
   * (테스트 더블·목)이 굳이 채우지 않아도 되게 둔다.
   */
  processing?: AppliedAudioProcessing | null
}

/**
 * 트랙이 보고하는 **적용 결과**를 읽는다(§4 관측 공백 메우기).
 *
 * `getSettings()` 는 지금 이 트랙에 실제로 걸린 값을, `getCapabilities()` 는
 * 장치가 할 수 있다고 밝힌 범위를 말한다. 둘 다 브라우저별로 빠진 키가 있고
 * `getCapabilities` 는 아예 없는 구현도 있다 — 그래서 전부 선택적으로 읽고,
 * 없으면 `undefined` 로 남긴다(모른다와 꺼졌다를 섞지 않는다).
 *
 * 순수 함수로 뽑아 둔 이유: jsdom 에 getUserMedia 가 없어 `MicCapture` 를
 * 통째로는 테스트할 수 없다(이 파일의 다른 테스트들과 같은 사정). 판정 규칙만
 * 떼어 두면 가짜 트랙으로 못박을 수 있다.
 */
export function readAudioProcessing(
  track: Pick<MediaStreamTrack, 'getSettings'> & {
    getCapabilities?: () => MediaTrackCapabilities
  },
): AppliedAudioProcessing {
  const settings = (track.getSettings?.() ?? {}) as MediaTrackSettings
  const out: AppliedAudioProcessing = {
    echoCancellation: settings.echoCancellation,
    noiseSuppression: settings.noiseSuppression,
    autoGainControl: settings.autoGainControl,
    channelCount: settings.channelCount,
  }
  // getCapabilities 는 구현이 없을 수도, 던질 수도 있다(일부 브라우저).
  try {
    const caps = track.getCapabilities?.()
    const agc = caps?.autoGainControl
    // 능력 목록에 true 가 들어 있으면 이 장치는 AGC 를 할 수 **있다**.
    if (agc !== undefined) out.supportsAutoGainControl = Array.from(agc).includes(true)
  } catch {
    /* 능력을 모른다 — 지어내지 않는다 */
  }
  return out
}

/**
 * 적용 결과를 **사람이 읽을 한 문장**으로 — 정상이면 null.
 *
 * 여기서 경고를 만드는 기준은 "요청한 것이 적용되지 않았다" 하나다. 특히
 * 자동 이득(AGC)이 꺼진 채로 도는 것은 화면에 아무 증상도 만들지 않으면서
 * 수음 레벨만 장치 설정에 맡기는 상태라, 관측되지 않으면 영영 모른다.
 *
 * **모르는 것은 경고하지 않는다**: 브라우저가 키를 보고하지 않으면
 * (`undefined`) 침묵한다 — 거짓 경고는 진짜 경고를 죽인다.
 */
export function audioProcessingNote(
  processing: AppliedAudioProcessing | null,
): string | null {
  if (!processing) return null
  const off: string[] = []
  if (processing.autoGainControl === false) off.push('자동 이득')
  if (processing.echoCancellation === false) off.push('에코 제거')
  if (processing.noiseSuppression === false) off.push('잡음 억제')
  if (off.length === 0) return null
  return `이 장치는 ${off.join('·')}을(를) 적용하지 않습니다 — 수음 레벨이 장치 설정에 좌우됩니다.`
}

/**
 * D1 — 캡처 구성 통지(제어 메시지 `capture_info`) 와이어 형식.
 *
 * **3값 문자열이다, 불리언이 아니다.** `AppliedAudioProcessing` 의
 * `undefined` 는 "모른다" 이고 `false` 로 접으면 안 된다(위 :93-94 주석).
 * JSON 불리언 축으로는 3값을 표현할 수 없으므로 문자열로 못박는다.
 */
export interface CaptureInfoWire {
  type: 'capture_info'
  processing: {
    ec: 'true' | 'false' | 'unknown'
    ns: 'true' | 'false' | 'unknown'
    agc: 'true' | 'false' | 'unknown'
  }
  /** `ctx.sampleRate` — 0 이면 미상(컨텍스트 없음) */
  sample_rate_hw: number
  /** FIR 폴백 사용 중인가 */
  resampled: boolean
  /** 이 시점의 전송 포맷 */
  codec: 'flac' | 'pcm'
  /** 선택 — 없으면 키 자체가 없다(모르는 것을 지어내지 않는다) */
  ua_family?: string
}

/** `true→'true'` / `false→'false'` / `undefined→'unknown'` */
function triState(value: boolean | undefined): 'true' | 'false' | 'unknown' {
  return value === undefined ? 'unknown' : value ? 'true' : 'false'
}

/**
 * D1 — 캡처 구성을 제어 메시지 페이로드로 만든다(순수 함수).
 *
 * jsdom 에 getUserMedia 가 없어 `MicCapture` 를 통째로는 테스트할 수 없다
 * (`readAudioProcessing`/`audioProcessingNote` 와 같은 사정) — 판정 규칙만
 * 떼어 두면 가짜 입력으로 못박을 수 있다.
 */
export function captureInfoPayload(input: {
  processing: AppliedAudioProcessing | null
  sampleRate: number
  resampled: boolean
  otf1: boolean
  ua?: string | undefined
}): CaptureInfoWire {
  return {
    type: 'capture_info',
    processing: {
      ec: triState(input.processing?.echoCancellation),
      ns: triState(input.processing?.noiseSuppression),
      agc: triState(input.processing?.autoGainControl),
    },
    sample_rate_hw: input.sampleRate,
    resampled: input.resampled,
    codec: input.otf1 ? 'flac' : 'pcm',
    ...(input.ua === undefined ? {} : { ua_family: input.ua }),
  }
}

/** 프레임 싱크 — 보통 WS. bufferedAmount 를 보고 적체를 판단한다. */
export interface CaptureSink {
  send(data: ArrayBuffer): void
  /** 제어 메시지(뮤트 등) — 없으면 보내지 않는다(테스트 더블 호환) */
  sendControl?(message: object): void
  readonly bufferedAmount: number
  readonly ready: boolean
  /**
   * A.6 — 이 소켓이 지금 OTF1 로 보내도 되는가(Transport.otf1Armed 를
   * 그대로 투영). 선택 필드다: 없으면 false 로 취급한다(PCM 전용 —
   * 캡을 아직 관측하지 못한 상태와 같다). 테스트 더블·목이 이 필드를
   * 안 채워도 기존처럼 동작해야 하므로 필수로 만들지 않는다.
   */
  readonly otf1Armed?: boolean
}

/**
 * §F1 — 프로덕션 훅(use-room-stream)이 실제로 채워 넣는 필드 전체를
 * 필수로 못박는다. CaptureSink 자체는 test double 호환을 위해 otf1Armed
 * 를 선택적으로 두지만(:214-217), 프로덕션 경로는 그 선택을 실수로
 * 빠뜨리면 안 된다 — 타입이 그 실수를 컴파일 타임에 잡는다.
 */
export type ProductionCaptureSink = Required<
  Pick<CaptureSink, 'send' | 'bufferedAmount' | 'ready' | 'otf1Armed'>
> &
  CaptureSink

export interface CaptureOptions {
  sink: CaptureSink
  /**
   * 드롭 구간을 상위에 알린다 — UI gap 마커·계수용(§4).
   *
   * `reason` 은 **필수**다: 선택으로 두면 호출부가 안 싣고 사유가 조용히
   * 사라진다. 두 사유는 원인도 버리는 쪽도 다르다 —
   *  · `'backlog'`      적체 드롭(소켓 살아 있음, **최신** 프레임을 버린다)
   *  · `'stall_evict'`  단절 보류 큐 축출(**가장 오래된** 것을 버린다)
   */
  onGap?: (gap: {
    from: number
    to: number
    frames: number
    reason: 'backlog' | 'stall_evict'
  }) => void
  onStats?: (stats: CaptureStats) => void
  /**
   * 캡처가 끊겼다/살아났다를 알린다. 화면은 이걸로 "마이크 입력이
   * 끊겼습니다 — 재연결 중" 을 띄운다. 자동 복구가 성공하면 alive=true 가
   * 뒤따르므로, 사용자는 보통 아무것도 하지 않아도 된다.
   */
  onLiveness?: (alive: boolean, reason?: string) => void
}

export class MicCapture {
  private stream: MediaStream | null = null
  private ctx: AudioContext | null = null
  private node: AudioWorkletNode | null = null
  private source: MediaStreamAudioSourceNode | null = null
  private readonly framer = new Framer()
  private readonly opts: CaptureOptions

  private sent = 0
  /** 만든 프레임 수 — 전송 결과와 무관한 단조 카운터(R2). */
  private framesGenerated = 0
  private droppedBacklog = 0
  private droppedStallEvict = 0
  private muted = false
  private resampled = false
  /** 브라우저가 실제로 적용한 처리 — 요청이 아니라 결과다(§4). */
  private processing: AppliedAudioProcessing | null = null
  /** 소켓이 끊긴 동안 붙잡아 둔 프레임(재개되면 순서대로 흘려보낸다) */
  private stalled: Frame[] = []
  private lastBlockAt = 0
  private broken = false
  /** 재시작은 했지만 아직 **실제 소리**를 못 본 상태(§4). */
  private awaitingSignal = false
  private watchdog: ReturnType<typeof setInterval> | null = null
  /** 복구 중복 실행 방지 — restart 는 한 번에 하나만 */
  private restarting = false
  private stopped = false
  /**
   * A.6/C3 — 이 브라우저가 FLAC WebCodecs 를 지원하는가. `start()` 에서
   * 한 번만 비동기로 판정해 캐시한다(매 프레임 재확인은 낭비). 판정이
   * 끝나기 전에는 PCM 으로만 나간다 — 안전한 기본값이다.
   */
  private flacCapable = false
  /** flac-encoder 세션 — 강등되면(§C2) 이후 프레임은 전부 PCM 폴백이다. */
  private flacSession: FlacEncoderSession | null = null
  /**
   * D1 — 직전에 보낸 `capture_info` 페이로드의 JSON 문자열(dedup).
   *
   * 같은 값을 반복해 보내지 않는다. 보내지 **못한** 경우(소켓 미준비)에는
   * 갱신하지 않으므로 다음 관측 지점에서 자동 재시도된다.
   */
  private lastCaptureInfoJson: string | null = null
  /**
   * encode() 에 들어갔지만 아직 청크로 안 돌아온 원본 PCM 프레임(seq 별).
   * 강등이 **비동기로**(WebCodecs error 콜백이 늦게 옴) 일어나면 이 안에
   * 남아 있는 프레임들이 orphanedSeqs 로 보고되고, 여기서 원본을 찾아
   * PCM 그대로 내보낸다(조용한 유실 금지 — 인코더 큐 안에서 드롭하지 않는다).
   */
  private readonly inFlightPcm = new Map<number, Frame>()
  // §4 레벨 미터 — 피크는 잠깐 유지해 눈에 보이게 한다(peak hold).
  private dbfs = SILENCE_DBFS
  private peakDbfs = SILENCE_DBFS
  private peakHoldUntil = 0

  constructor(options: CaptureOptions) {
    this.opts = options
  }

  get stats(): CaptureStats {
    return {
      sent: this.sent,
      framesGenerated: this.framesGenerated,
      // 합계는 파생이다 — 두 카운터가 진실이므로 불변식이 구조적으로 성립한다.
      dropped: this.droppedBacklog + this.droppedStallEvict,
      droppedBacklog: this.droppedBacklog,
      droppedStallEvict: this.droppedStallEvict,
      buffered: this.opts.sink.bufferedAmount,
      sampleRate: this.ctx?.sampleRate ?? 0,
      resampled: this.resampled,
      muted: this.muted,
      // 뮤트 중에는 0 으로 — 꺼진 마이크가 살아 있는 것처럼 보이면 안 된다.
      dbfs: this.muted ? SILENCE_DBFS : this.dbfs,
      peakDbfs: this.muted ? SILENCE_DBFS : this.peakDbfs,
      stalled: this.stalled.length,
      lastBlockAgeMs: this.lastBlockAt === 0 ? 0 : Date.now() - this.lastBlockAt,
      broken: this.broken,
      processing: this.processing,
    }
  }

  async start(): Promise<void> {
    if (this.ctx) return
    this.stopped = false
    // A.6/C3 — 세션 시작 시 한 번만 판정한다. 결과가 오기 전 프레임은
    // flacCapable=false 인 채로 안전하게 PCM 을 탄다.
    this.flacCapable = await isFlacSupported()
    try {
      await this.open()
    } catch (err) {
      // 실패 단계를 실어 던진다(host 의 원인별 안내용) — 동일 객체를 그대로 재던진다.
      if (err && typeof err === 'object') {
        ;(err as { micStage?: string }).micStage = this.openStage
      }
      throw err
    }
    // 워치독 — 트랙 이벤트가 안 오는 고장(컨텍스트 suspend, 워크릿 침묵)까지
    // 잡으려면 주기적 확인이 필요하다. 이벤트만 믿으면 조용한 죽음을 놓친다.
    this.lastBlockAt = Date.now()
    this.watchdog = setInterval(() => this.checkLiveness(), 500)
  }

  /** 실제 장치·그래프를 연다(최초 start 와 복구 재시작이 공유한다). */
  private openStage: 'getUserMedia' | 'audiocontext' | 'worklet' = 'getUserMedia'

  private async open(): Promise<void> {
    this.openStage = 'getUserMedia'
    // §4 캡처 제약 — 에코 제거·잡음 억제·자동 이득 on
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
      },
      video: false,
    })

    this.openStage = 'audiocontext'
    // 1차: 네이티브 16k 리샘플러에 맡긴다(품질·비용 최적).
    let ctx: AudioContext
    try {
      ctx = new AudioContext({ sampleRate: TARGET_RATE })
    } catch {
      // 미지원 브라우저 — 기본 레이트로 열고 워크릿 FIR 폴백을 쓴다.
      ctx = new AudioContext()
    }
    // 요청이 무시된 경우도 있다(생성은 성공하지만 레이트가 다름).
    this.resampled = ctx.sampleRate !== TARGET_RATE
    this.ctx = ctx

    if (ctx.state === 'suspended') await ctx.resume()

    this.openStage = 'worklet'
    await ctx.audioWorklet.addModule(workletUrl)
    const node = new AudioWorkletNode(ctx, 'omnitrans-capture', {
      numberOfInputs: 1,
      numberOfOutputs: 0,
      channelCount: 1,
      channelCountMode: 'explicit',
    })
    node.port.onmessage = (event: MessageEvent<CaptureMessage>) => {
      if (event.data.type === 'audio') this.onSamples(event.data.samples)
    }
    this.node = node

    this.source = ctx.createMediaStreamSource(this.stream)
    this.source.connect(node)

    // ── 죽음을 관측한다 ────────────────────────────────────────────────
    // 트랙이 끝나면(장치 뽑힘·블루투스 핸드오프·OS 가 회수) 워크릿은 조용히
    // 무음만 올려보내거나 아예 멈춘다. 둘 다 화면에는 "정상"으로 보인다.
    for (const track of this.stream.getAudioTracks()) {
      track.onended = () => this.fail('track_ended')
      track.onmute = () => this.fail('track_muted')
    }

    // 요청한 제약이 **실제로 걸렸는지** 읽어 둔다(§4). 위 getUserMedia 는
    // AGC·에코제거·잡음억제를 요청하지만, 장치가 무시해도 오류가 나지 않아
    // 지금까지 아무도 결과를 확인하지 않았다 — 레이트(`resampled`)는 확인하면서
    // 처리 항목은 확인하지 않던 비대칭을 메운다. 판정하지 않고 관측만 한다.
    const [first] = this.stream.getAudioTracks()
    this.processing = first ? readAudioProcessing(first) : null
    // 탭 백그라운드화·자동재생 정책으로 컨텍스트가 멈추면 프레임이 0 이 된다.
    ctx.onstatechange = () => {
      if (this.stopped) return
      if (ctx.state === 'suspended') {
        // 먼저 그냥 깨워 본다 — 대부분 이걸로 끝난다(전 경로 재생성보다 싸다).
        void ctx.resume().catch(() => this.fail('context_suspended'))
      } else if (ctx.state === 'closed') {
        this.fail('context_closed')
      }
    }

    // D1 — 적용값이 확정된 지금이 이 구성을 알릴 시점이다.
    this.sendCaptureInfo()
  }

  /**
   * 캡처가 죽었다고 판정 — 알리고 자동 복구한다.
   *
   * 여기가 "새로고침해야 고쳐지던" 문제의 해소 지점이다. 사용자는 아무것도
   * 누르지 않는다.
   */
  private fail(reason: string): void {
    if (this.stopped || this.restarting) return
    this.broken = true
    this.opts.onLiveness?.(false, reason)
    this.opts.onStats?.(this.stats)
    void this.restart()
  }

  /** 전 경로(스트림·컨텍스트·워크릿)를 새로 만든다. seq 는 이어 간다. */
  private async restart(): Promise<void> {
    if (this.restarting || this.stopped) return
    this.restarting = true
    try {
      await this.teardown()
      if (this.stopped) return
      await this.open()
      // D1 — 장치가 바뀌면 적용값도 바뀐다(open() 안의 통지는 dedup 이 흡수하고,
      // 값이 실제로 달라졌을 때만 다시 나간다).
      this.sendCaptureInfo()
      // 살아났다. 뮤트 상태는 사용자의 의도이므로 그대로 복원한다.
      if (this.muted) {
        for (const track of this.stream?.getAudioTracks() ?? []) track.enabled = false
        this.node?.port.postMessage({ type: 'mute', muted: true })
      }
      this.lastBlockAt = Date.now()
      // **아직 살아났다고 말하지 않는다.** 그래프를 다시 세운 것과 소리가
      // 실제로 들어오는 것은 다르다 — 실측: 장치가 회수된 뒤 재시작하면
      // getUserMedia 는 성공하고 프레임도 계속 나가지만 내용은 전부 무음이다.
      // 여기서 곧바로 복구를 선언하면 화면이 "정상" 을 그리고, 사용자는
      // 죽은 마이크 앞에서 계속 말한다(오늘 사고의 정확한 모양이다).
      // 실제 소리가 한 블록이라도 오면 `onSamples` 가 복구를 선언한다.
      this.awaitingSignal = true
    } catch {
      // 장치가 아직 안 돌아왔다 — 워치독이 다음 주기에 다시 시도한다.
      this.broken = true
    } finally {
      this.restarting = false
      this.opts.onStats?.(this.stats)
    }
  }

  /**
   * 주기 점검 — 이벤트 없이 조용히 멈춘 경우를 잡는다.
   *
   * 뮤트 중에는 워크릿이 의도적으로 블록을 안 올리므로 침묵이 정상이다.
   */
  private checkLiveness(): void {
    if (this.stopped || this.restarting) return
    if (!this.muted && Date.now() - this.lastBlockAt > STALL_MS) {
      this.fail('no_audio_blocks')
      return
    }
    // 소켓이 살아났으면 보류분을 먼저 흘려보낸다(재개는 프레임 도착과
    // 무관하게 일어나야 한다 — 마침 조용한 구간이면 push 가 안 온다).
    if (this.stalled.length > 0 && this.opts.sink.ready) this.drainStalled()
    // 보류가 쌓이는 동안에도 화면이 사실을 알아야 한다.
    if (this.stalled.length > 0) this.opts.onStats?.(this.stats)
  }

  /** 보류 프레임을 순서대로 내보낸다 — 손실 0 으로 블립을 넘긴다. */
  private drainStalled(): void {
    const pending = this.stalled
    this.stalled = []
    for (const frame of pending) this.emit(frame)
  }

  /** §4 뮤트 — 캡처 자체 중단(트랙 disable). 서버 도달 전 차단된다. */
  setMuted(muted: boolean): void {
    this.muted = muted
    for (const track of this.stream?.getAudioTracks() ?? []) {
      track.enabled = !muted
    }
    this.node?.port.postMessage({ type: 'mute', muted })
    if (muted) {
      // 진행 중 버퍼는 flush 한다(§5 VAD 리셋 조건).
      const tail = this.framer.flush()
      if (tail) this.encodeAndEmit(tail)
      // 서버에도 알린다(§4): 트랙을 끄면 프레임이 그냥 끊길 뿐이라, 서버는
      // 그것을 조용한 구간과 구분하지 못하고 적응 무음 타임아웃까지 기다린다.
      // 그 사이 말하다 끊긴 발화가 화면에 뜨지 않는다. 서버는 이 신호를 받고
      // 진행 중 발화를 즉시 flush 한다(버리지 않는다).
      this.opts.sink.sendControl?.({ type: 'mute' })
    }
    this.opts.onStats?.(this.stats)
  }

  async stop(): Promise<void> {
    this.stopped = true
    if (this.watchdog) {
      clearInterval(this.watchdog)
      this.watchdog = null
    }
    const tail = this.framer.flush()
    if (tail) this.encodeAndEmit(tail)
    // 마지막 기회 — 보류분이 남아 있고 소켓이 살아 있으면 보내고 끝낸다.
    if (this.stalled.length > 0 && this.opts.sink.ready) this.drainStalled()
    await this.teardown()
    // 세션이 끝났다 — 인코더를 닫기 전에, 아직 청크로 안 돌아온 프레임이
    // 있으면 PCM 으로 대신 내보낸다. close() 는 그 청크 콜백이 온다는
    // 보장이 없으므로, 여기서 먼저 정리하지 않으면 마지막 프레임이 조용히
    // 사라질 수 있다(조용한 유실 금지 — stop() 이라고 예외가 아니다).
    if (this.inFlightPcm.size > 0) {
      for (const orphan of this.inFlightPcm.values()) this.emit(orphan)
      this.inFlightPcm.clear()
    }
    this.flacSession?.close()
    this.flacSession = null
  }

  /** 그래프만 해체한다(복구 재시작이 공유 — 여기서 stopped 를 건드리지 않는다). */
  private async teardown(): Promise<void> {
    for (const track of this.stream?.getAudioTracks() ?? []) {
      track.onended = null
      track.onmute = null
      track.stop()
    }
    if (this.ctx) this.ctx.onstatechange = null
    this.node?.port.close()
    this.node?.disconnect()
    this.source?.disconnect()
    // 이미 닫힌 컨텍스트를 닫으면 throw 한다 — 복구 경로를 막지 않게 삼킨다.
    try {
      await this.ctx?.close()
    } catch {
      /* 이미 닫혔다 */
    }
    this.ctx = null
    this.node = null
    this.source = null
    this.stream = null
  }

  private onSamples(samples: Float32Array): void {
    // 살아 있다는 유일한 증거 — 워치독이 이 시각을 본다.
    this.lastBlockAt = Date.now()
    // §4 레벨 — 프레임 조립과 같은 블록에서 계산한다(추가 트래픽 없음).
    this.dbfs = blockDbfs(samples)
    // 재시작 후 **첫 실제 소리**에서만 복구를 선언한다(무음 복구 오인 방지).
    // 뮤트는 사용자가 의도한 무음이므로 그 상태에서는 기다리지 않는다.
    if (this.awaitingSignal && (this.muted || this.dbfs > SIGNAL_FLOOR_DBFS)) {
      this.awaitingSignal = false
      this.broken = false
      this.opts.onLiveness?.(true)
    }
    const peak = rmsToDbfs(blockPeak(samples))
    const now = Date.now()
    // peak hold: 순간 피크가 눈에 보이도록 잠깐 붙잡는다.
    if (peak >= this.peakDbfs || now > this.peakHoldUntil) {
      this.peakDbfs = peak
      this.peakHoldUntil = now + 800
    }

    const frames = this.framer.push(floatToPcm16(samples))
    for (const frame of frames) this.encodeAndEmit(frame)
    // 프레임 경계와 무관하게 ~16fps 로 레벨을 올린다(미터가 뚝뚝 끊기지 않게).
    if (frames.length > 0 || now - this.lastStatsAt > 60) {
      this.lastStatsAt = now
      this.opts.onStats?.(this.stats)
    }
  }

  private lastStatsAt = 0

  /**
   * C3 — PCM 프레임을 (필요하면) OTF1 로 압축한 뒤 emit() 에 넘긴다.
   *
   * emit() 자체는 건드리지 않는다: emit() 은 **wire 버퍼가 이미 확정된**
   * `Frame` 만 알면 되고, 그 정책(적체 드롭·소켓 단절 보류·seq 갭)은 PCM 이든
   * OTF1 이든 완전히 동일해야 한다(SPEC C3). seq 는 Framer 가 이미 발급했으므로
   * 여기서 드롭하더라도(=인코딩만 하고 emit 을 안 부르는 경우는 없다 — 항상
   * emit 으로 넘긴다) seq 재사용은 일어나지 않는다.
   *
   * FLAC 인코딩은 WebCodecs 콜백을 통해 **비동기로** 돌아온다. 그 사이
   * emit() 을 부르지 않고 청크가 도착했을 때 부른다 — "인코더 큐 안에서는
   * 절대 드롭하지 않는다"(SPEC) 는 이 콜백이 반드시 오거나, 강등 신호가
   * 반드시 온다는 뜻이다(둘 중 하나는 always happens, FlacEncoderSession 계약).
   *
   * F3 — **소켓이 끊긴 상태(`!sink.ready`)는 armed 여부와 무관하게 먼저
   * 걸러 원본 PCM 을 그대로 emit() 한다.** 이 판단은 encode() 호출 전에
   * 동기로 끝나야 한다(비동기 encode() 의 `.then`/`await` 안에서 하면 그
   * 사이 이미 인코더 큐에 프레임이 들어간 뒤다) — stall 버퍼(`this.stalled`)
   * 는 PCM 프레임만 보유한다는 불변식이 여기서 나온다. armed 판정은 ready
   * 판정 다음이다.
   */
  /**
   * D1 — 캡처 구성을 서버에 알린다(진단 사이드카용, 응답 없음).
   *
   * 뮤트와 같은 패턴이다(`setMuted` 의 `sendControl`): 정보의 전 출처가 이
   * 클래스 안에만 있으므로 상위 콜백을 거치지 않는다.
   *
   * **실패는 삼킨다** — 진단 통지가 캡처를 멈추면 안 된다. 소켓이 아직
   * 안 붙었으면 보내지 않고 dedup 기억도 갱신하지 않는다(다음 관측 지점에서
   * 자동 재시도).
   */
  private sendCaptureInfo(): void {
    const sink = this.opts.sink
    if (sink.ready === false) return
    const payload = captureInfoPayload({
      processing: this.processing,
      sampleRate: this.ctx?.sampleRate ?? 0,
      resampled: this.resampled,
      // 강등된 세션은 이후 프레임이 전부 PCM 이다(§C2) — 이 시점의 **실제**
      // 전송 포맷이 codec 의 정의이므로 강등도 함께 본다.
      otf1:
        this.flacCapable &&
        sink.otf1Armed === true &&
        this.flacSession?.isDowngraded !== true,
    })
    const json = JSON.stringify(payload)
    if (json === this.lastCaptureInfoJson) return
    try {
      sink.sendControl?.(payload)
      this.lastCaptureInfoJson = json
    } catch {
      /* 진단 통지 실패는 캡처를 멈추지 않는다 */
    }
  }

  private encodeAndEmit(frame: Frame): void {
    // 만든 순간에 센다(NIT-A) — 전송 결과를 기다리면 단절 보류분이 빠진다(R2).
    // mute(:595)/stop(:612) 의 flush() 꼬리 프레임도 이 단일 지점을 지나므로
    // 함께 세어진다.
    this.framesGenerated += 1
    const sink = this.opts.sink

    if (!sink.ready) {
      this.emit(frame)
      return
    }

    const armed = this.flacCapable && sink.otf1Armed === true

    // D1 — codec 전이 관측(armed 최초 true / 강등 최초 관측). dedup 이
    // 같은 값의 재전송을 막으므로 회의 1건당 최대 2~3회다.
    this.sendCaptureInfo()

    if (!armed) {
      this.emit(frame)
      return
    }

    if (!this.flacSession) {
      this.flacSession = new FlacEncoderSession({
        onDowngrade: (_reason, orphanedSeqs) => {
          // 이미 encode() 에 들어갔지만 청크를 못 받은 프레임들 — 원본을
          // inFlightPcm 에서 찾아 PCM 그대로 내보낸다(조용한 유실 금지).
          for (const seq of orphanedSeqs) {
            const orphan = this.inFlightPcm.get(seq)
            this.inFlightPcm.delete(seq)
            if (orphan) this.emit(orphan)
          }
        },
      })
      this.flacSession.init((chunk) => {
        // chunk.seq 의 원본 PCM 프레임을 찾는다 — 세션을 만든 시점의 `frame`
        // 클로저를 쓰면 안 된다(그 프레임은 이미 오래전에 처리됐다). 없으면
        // 강등 경로(onDowngrade)가 이 seq 를 이미 PCM 으로 내보낸 뒤다 —
        // 늦게 도착한 청크를 또 emit 하면 서버에 같은 seq 가 중복된다.
        const orig = this.inFlightPcm.get(chunk.seq)
        if (!orig) return // 강등 경로가 이미 이 seq 를 PCM 으로 내보냈다 — 중복 방출 금지
        this.inFlightPcm.delete(chunk.seq)
        // 0청크(=chunk.bytes===null) — 이 프레임만 PCM 으로 대신 보낸다
        // (세션 강등이 아니다, F2c). 그 외에는 인코더 콜백 — 압축 프레임이
        // 준비됐다. >=10244B 면 PCM 크기와 같거나 커진 것이므로 압축
        // 이득이 없다 — 이 프레임만 PCM 으로 대신 보낸다(§1 클라이언트
        // 규칙, 이 프레임의 원본 PCM 프레임을 그대로 쓰면 된다 — Framer 가
        // 이미 만들어 둔 것).
        if (chunk.bytes === null) {
          this.emit(orig)
          return
        }
        // F-B2 — 제출 시점에는 sink.ready 였지만, flush() 가 비동기로 도는
        // 사이 소켓이 끊겼을 수 있다(encodeAndEmit 의 ready 검사는 제출
        // "전"에만 본다). 여기서 다시 확인하지 않으면 압축 프레임이 그대로
        // stalled 버퍼에 들어가 "적체 버퍼는 PCM 전용" 불변식이 깨진다 —
        // 재확인해 미준비면 원본 PCM 을 대신 보낸다(emit() 이 알아서 보류).
        if (!sink.ready) {
          this.emit(orig)
          return
        }
        const packed = packCompressed(chunk.seq, chunk.bytes, OTF1_NSAMP, 0)
        if (packed.byteLength >= LEGACY_FRAME_BYTES) {
          this.emit(orig)
          return
        }
        this.emit({ seq: chunk.seq, buffer: packed })
      })
    }

    if (this.flacSession.isDowngraded) {
      // 이미 강등된 세션 — 이후 프레임은 전부 PCM (seq 는 그대로 이어간다,
      // 가짜 갭을 만들지 않는다).
      this.emit(frame)
      return
    }

    // Framer.pack 이 만든 wire 버퍼에서 seq 헤더(4B)를 떼고 순수 PCM16 샘플만
    // 인코더에 넣는다. Framer/pack 자체는 건드리지 않는다(§5 규칙 4).
    const samples = new Int16Array(frame.buffer.slice(SEQ_HEADER_BYTES))
    this.inFlightPcm.set(frame.seq, frame)
    // encode() 도중(동기든 나중 콜백이든) 강등되면 onDowngrade 의
    // orphanedSeqs 경로가 inFlightPcm 에서 이 프레임을 찾아 emit 한다 —
    // 여기서 따로 처리할 것이 없다(프레임을 삼키지 않는다는 계약은
    // FlacEncoderSession 이 보장한다).
    //
    // 샘플은 이미 16k 로 리샘플되어 있다 — ctx.sampleRate(하드웨어 레이트)를
    // 넘기면 인코더 구성(16000)과 어긋나 첫 프레임에서 영구 강등된다.
    //
    // encode() 는 비동기(F2c)지만 여기서 await 하지 않는다 — encodeAndEmit
    // 은 워크릿 콜백 한 블록당 동기로 끝나야 하고(다음 블록이 곧 온다),
    // 청크/PCM 폴백 신호는 onChunk 콜백을 통해 나중에 emit() 된다.
    void this.flacSession.encode(frame.seq, samples, TARGET_RATE)
  }

  /**
   * 전송 — 단, **소켓 단절과 적체는 다른 사건이다**.
   *
   *  · 소켓이 끊김(`ready=false`): 네트워크 블립이다. 곧 재접속된다는 전제로
   *    프레임을 보류한다. 이전 구현은 여기서 전량 버렸고, 재접속 후에도
   *    아무도 캡처를 다시 물리지 않아 영구 침묵이 됐다 — 신고된 증상 그 자체다.
   *  · 버퍼 적체(`bufferedAmount` 초과): 소켓은 살아 있는데 대역이 못 따라간다.
   *    이때 보류는 시차만 키우므로 예전대로 드롭하고 갭을 낸다.
   */
  private emit(frame: Frame): void {
    const sink = this.opts.sink

    if (!sink.ready) {
      this.stalled.push(frame)
      // 8초를 넘겨도 안 돌아오면 오래된 것부터 포기한다 — 최신 음성이 회의의
      // 현재다. 버린 만큼은 갭으로 표면화한다(조용한 유실 금지).
      while (this.stalled.length > STALL_BUFFER_FRAMES) {
        const evicted = this.stalled.shift() as Frame
        this.droppedStallEvict += 1
        this.opts.onGap?.({
          from: evicted.seq, to: evicted.seq + 1, frames: 1, reason: 'stall_evict',
        })
      }
      return
    }

    // 소켓이 살아났는데 보류분이 남아 있으면 **그것부터** 내보낸다(순서 보존).
    if (this.stalled.length > 0) this.drainStalled()

    if (sink.bufferedAmount > BUFFER_LIMIT_BYTES) {
      this.droppedBacklog += 1
      // 이 프레임의 seq 는 이미 발급됐다 — 그대로 버려 서버 seq 갭을 만든다.
      this.opts.onGap?.({
        from: frame.seq, to: frame.seq + 1, frames: 1, reason: 'backlog',
      })
      return
    }
    sink.send(frame.buffer)
    this.sent += 1
  }
}

/**
 * §4 캡처 상태를 **사람이 읽을 한 문장**으로 — 없으면 null(정상).
 *
 * 화면이 이 문장을 필요로 하는 이유: 마이크 버튼이 켜져 있고 레벨 미터가
 * 움직이지 않으면 사용자는 "조용한 회의" 와 "죽은 마이크" 를 구분할 수 없다.
 * 미터는 마지막 값에 **얼어붙은 채** 남으므로 미터만으로는 알 수 없다.
 *
 * 두 고장을 구분해 말한다 — 사용자가 할 일이 달라서가 아니라(둘 다 자동
 * 복구다) **어디가 끊겼는지**가 다르기 때문이다:
 *   · `stale` — 캡처(마이크) 쪽이 죽었다. `onLiveness(false)` 가 알려 준다.
 *   · `stalled > 0` — 소켓 쪽이 끊겼다. 이때 오디오는 버려지지 않고 보류되며
 *     다시 붙으면 순서대로 흘러간다(무손실 재개). 그 사실을 말해 주면
 *     사용자가 말을 멈추거나 같은 말을 반복하지 않는다.
 *
 * 마이크 고장을 먼저 말한다: 캡처가 죽었으면 보낼 것 자체가 없으므로
 * 보류 안내는 그 상황에서 오해를 부른다.
 */
export function captureStatusNote(input: {
  stale: string | null
  stats: CaptureStats | null
}): string | null {
  if (input.stale) return input.stale
  // `broken` 은 **아직 복구되지 않았다** 는 정본 신호다(§4). 재시작에 성공해도
  // 실제 소리를 못 봤으면 여기가 참으로 남는다 — onLiveness 콜백 한 번만
  // 믿으면 그 상태를 놓치고 화면이 "정상" 을 그린다(실측 사고의 모양).
  if (input.stats?.broken) {
    return '마이크 입력이 끊겼습니다 — 자동으로 재연결 중입니다.'
  }
  if (input.stats && input.stats.stalled > 0) {
    return '연결이 끊겨 음성을 잠시 보관하고 있습니다 — 다시 연결되면 이어서 전송합니다.'
  }
  return null
}
